import React from "react";

import { Alert, Button, Checkbox, Form, Icon, Input, InputNumber, Modal, Radio, Spin, Table, Upload, message } from "antd";

import { _postSupplierPriceListImportCommit, _postSupplierPriceListImportParse } from "../../services";
import { errorMessage } from "../term-contract/helpers";
import { showUploadResults } from "../term-contract/lines";
import { UploadFields, uploadFieldsBody, uploadFieldsValue } from "./uploads";
import { IMPORT_ACCEPT, PREVIEW_PAGE_SIZE, mappingLines, money, rowSourceKey, rowsToCommit, sourceLabel, warningText } from "./helpers";

const MUTED = "rgba(0,0,0,0.45)";
const TEXT_COLUMNS = [
    { field: "supplierCode", title: "Code", width: 120 },
    { field: "title", title: "Title", width: 260 },
    { field: "brand", title: "Brand", width: 110 },
    { field: "model", title: "Model", width: 120 },
    { field: "spec", title: "Spec", width: 140 },
    { field: "uom", title: "UOM", width: 70 }
];
const NUMBER_COLUMNS = [
    { field: "costPrice", title: "Cost" },
    { field: "listPrice", title: "List" },
    { field: "moq", title: "MOQ" }
];

/** "≥10: $4.00" per tier column group of a parsed row (tier1MinQty, tier1CostPrice, …). */
const rowTiers = ({
    row,
}) => [1, 2, 3].filter(n => row[`tier${n}MinQty`])
    .map(n => `≥${row[`tier${n}MinQty`]}: ${money({ value: row[`tier${n}CostPrice`] !== null && row[`tier${n}CostPrice`] !== undefined ? row[`tier${n}CostPrice`] : row[`tier${n}ListPrice`] })}`)
    .join(" / ");

/**
 * The AI import's preview: warnings and the column mapping on top, price conflicts to settle (Save waits for every one),
 * then the rows, editable, 100 a page, with edits and deletions kept in state until Save commits what is left.
 */
export class ImportPreview extends React.Component {

    state = {
        rows: this.props.parsed.rows.map((row, i) => ({ ...row, _key: `r${i}` })),
        chosen: {},
        page: 1,
        warningsOnly: false,
        fields: uploadFieldsValue(this.props.parsed.suggested || {}),
        saving: false
    };

    warnedRows = new Set(this.props.parsed.warnings.filter(w => w.sourceRow !== undefined && w.sourceRow !== null).map(w => rowSourceKey({ item: w })));

    /* the kept row joins the grid (so it can be edited there); choosing again replaces it */
    choose = ({
        conflictIndex,
        rowIndex,
    }) => this.setState(state => ({
        chosen: { ...state.chosen, [conflictIndex]: rowIndex },
        rows: [
            ...state.rows.filter(row => row._conflict !== conflictIndex),
            { ...this.props.parsed.conflicts[conflictIndex].rows[rowIndex], _key: `c${conflictIndex}`, _conflict: conflictIndex }
        ]
    }));

    setCell = ({
        key,
        field,
        value,
    }) => this.setState(state => ({ rows: state.rows.map(row => row._key === key ? { ...row, [field]: value } : row) }));

    /* deleting a conflict's kept row un-settles that conflict, so it is picked again rather than silently left out */
    deleteRow = ({
        key,
    }) => this.setState(state => {
        const removed = state.rows.find(row => row._key === key);
        const chosen = { ...state.chosen };
        if (removed && removed._conflict !== undefined) delete chosen[removed._conflict];
        return { rows: state.rows.filter(row => row._key !== key), chosen };
    });

    save = async () => {
        const { priceList, parsed, onCommitted } = this.props;
        const { rows, fields } = this.state;
        const body = uploadFieldsBody({ value: fields });
        if (!body) {
            message.error("File date and valid to are required.");
            return;
        }
        this.setState({ saving: true });
        try {
            const { data } = await _postSupplierPriceListImportCommit({
                id: priceList._id,
                data: { importToken: parsed.importToken, rows: rowsToCommit({ rows }), ...body }
            });
            /* a result's row is its position in the rows sent: name its source instead */
            const keyColumn = {
                title: "Line (source)",
                key: "line",
                render: (_, result) => {
                    const sent = result.row ? rows[result.row - 1] : null;
                    return sent ? `${result.lineKey || ""} (${sourceLabel({ item: sent })})` : result.lineKey;
                }
            };
            showUploadResults({ results: data.results, warnings: data.warnings, keyColumn });
            onCommitted();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            /* the server used up the token: a retry cannot work, so the preview closes (its message says to parse again) */
            if (/expired/i.test(errorMessage({ error: e }))) {
                this.props.onCancel();
                return;
            }
            this.setState({ saving: false });
        }
    };

    columns = () => [
        { title: "Source", key: "source", width: 160, render: (_, row) => <span style={{ fontSize: 12, color: MUTED }}>{sourceLabel({ item: row })}</span> },
        ...TEXT_COLUMNS.map(c => ({
            title: c.title,
            key: c.field,
            width: c.width,
            render: (_, row) => (
                <Input size="small" aria-label={c.field} value={row[c.field] || ""}
                    onChange={e => this.setCell({ key: row._key, field: c.field, value: e.target.value })} />
            )
        })),
        ...NUMBER_COLUMNS.map(c => ({
            title: c.title,
            key: c.field,
            width: 100,
            render: (_, row) => (
                <InputNumber size="small" min={0} aria-label={c.field} style={{ width: 90 }}
                    value={row[c.field] === null ? undefined : row[c.field]}
                    onChange={value => this.setCell({ key: row._key, field: c.field, value: typeof value === "number" ? value : null })} />
            )
        })),
        { title: "Tiers", key: "tiers", width: 140, render: (_, row) => <span style={{ fontSize: 12 }}>{rowTiers({ row }) || "—"}</span> },
        {
            title: "Remarks",
            key: "remarks",
            width: 160,
            render: (_, row) => (
                <Input size="small" aria-label="remarks" value={row.remarks || ""}
                    onChange={e => this.setCell({ key: row._key, field: "remarks", value: e.target.value })} />
            )
        },
        { title: "Link", key: "link", width: 110, render: (_, row) => <span style={{ fontSize: 12 }}>{row.sku ? `SKU ${row.sku}` : row.productId || ""}</span> },
        {
            title: "",
            key: "delete",
            width: 44,
            render: (_, row) => (
                <Button size="small" type="link" icon="delete" aria-label="Delete row" title="Delete row" style={{ color: MUTED }}
                    onClick={() => this.deleteRow({ key: row._key })} />
            )
        }
    ];

    renderConflicts() {
        const { conflicts } = this.props.parsed;
        const { chosen } = this.state;
        if (!conflicts.length) return null;
        return (
            <div style={{ marginBottom: 16 }}>
                <h3>Price conflicts: keep one row of each ({Object.keys(chosen).length} of {conflicts.length} settled)</h3>
                {conflicts.map((conflict, conflictIndex) => (
                    <div key={conflict.lineKey} style={{ border: "1px solid #ffd591", background: "#fff7e6", borderRadius: 4, padding: 8, marginBottom: 8 }}>
                        <b>{conflict.supplierCode || conflict.lineKey}</b>
                        <Radio.Group
                            style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 4 }}
                            value={chosen[conflictIndex]}
                            onChange={e => this.choose({ conflictIndex, rowIndex: e.target.value })}
                        >
                            {conflict.rows.map((row, rowIndex) => (
                                <Radio key={rowIndex} value={rowIndex}>
                                    {row.title} · cost {money({ value: row.costPrice })} · list {money({ value: row.listPrice })}
                                    <div style={{ fontSize: 12, color: MUTED, marginLeft: 24 }}>{sourceLabel({ item: row })}</div>
                                </Radio>
                            ))}
                        </Radio.Group>
                    </div>
                ))}
                <div style={{ fontSize: 12, color: MUTED }}>The kept row joins the grid below, where it can be edited.</div>
            </div>
        );
    }

    render() {
        const { parsed, onCancel } = this.props;
        const { rows, chosen, page, warningsOnly, fields, saving } = this.state;
        const unsettled = parsed.conflicts.length - Object.keys(chosen).length;
        const shown = warningsOnly ? rows.filter(row => this.warnedRows.has(rowSourceKey({ item: row }))) : rows;
        const canSave = unsettled === 0 && rows.length > 0 && !saving;
        return (
            <Modal
                visible
                width="95%"
                style={{ top: 20 }}
                title="Import preview"
                onCancel={onCancel}
                footer={[
                    <span key="why" style={{ color: MUTED, marginRight: 12 }}>
                        {unsettled ? `Settle ${unsettled} price conflict${unsettled === 1 ? "" : "s"} first` : `${rows.length.toLocaleString()} rows will be saved`}
                    </span>,
                    <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                    <Button key="save" type="primary" disabled={!canSave} loading={saving} onClick={this.save}>Save</Button>
                ]}
            >
                {parsed.looksLikeQuotation && (
                    <Alert type="error" showIcon style={{ marginBottom: 8 }}
                        message="This looks like a quotation: only standing price lists belong here; nothing imported from it." />
                )}
                {parsed.warnings.length > 0 && (
                    <Alert type="warning" style={{ marginBottom: 8 }}
                        message={`${parsed.warnings.length} warning${parsed.warnings.length === 1 ? "" : "s"}`}
                        description={<div style={{ maxHeight: 160, overflowY: "auto" }}>{parsed.warnings.map((w, i) => <div key={i}>{warningText({ warning: w })}</div>)}</div>} />
                )}
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>
                    {mappingLines({ mappings: parsed.mappings }).map(line => <div key={line}>{line}</div>)}
                    <div>A wrong column? Fix the rows below, or prepare the template by hand and use Upload template.</div>
                </div>
                {this.renderConflicts()}
                <Form layout="vertical">
                    <UploadFields value={fields} onChange={value => this.setState({ fields: value })} />
                </Form>
                <div style={{ margin: "12px 0" }}>
                    <Checkbox checked={warningsOnly} onChange={e => this.setState({ warningsOnly: e.target.checked, page: 1 })}>Rows with warnings only</Checkbox>
                </div>
                <Table
                    rowKey="_key"
                    size="small"
                    dataSource={shown}
                    columns={this.columns()}
                    scroll={{ x: 1800 }}
                    pagination={{ current: page, pageSize: PREVIEW_PAGE_SIZE, total: shown.length, onChange: p => this.setState({ page: p }) }}
                />
            </Modal>
        );
    }
}

/** "Import supplier file": pick one or more files → parse (a big PDF takes ~30s) → the preview. */
export class ImportButton extends React.Component {

    state = { parsing: false, parsed: null };

    parse = async ({
        files,
    }) => {
        this.setState({ parsing: true });
        try {
            const { data } = await _postSupplierPriceListImportParse({ id: this.props.priceList._id, files });
            this.setState({ parsed: data });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ parsing: false });
        }
    };

    render() {
        const { priceList, onImported } = this.props;
        const { parsing, parsed } = this.state;
        return (
            <span>
                {/* beforeUpload runs once per file; parse the whole pick once, on its last file. false: we post the files ourselves */}
                <Upload multiple accept={IMPORT_ACCEPT} showUploadList={false}
                    beforeUpload={(file, fileList) => { if (file === fileList[fileList.length - 1]) this.parse({ files: fileList }); return false; }}>
                    <Button type="primary" loading={parsing}><Icon type="import" /> Import supplier file</Button>
                </Upload>
                {parsing && (
                    <Modal visible closable={false} footer={null} maskClosable={false}>
                        <Spin /> <span style={{ marginLeft: 8 }}>Reading the file… a big PDF can take about 30 seconds.</span>
                    </Modal>
                )}
                {parsed && (
                    <ImportPreview
                        priceList={priceList}
                        parsed={parsed}
                        onCancel={() => this.setState({ parsed: null })}
                        onCommitted={() => {
                            this.setState({ parsed: null });
                            onImported();
                        }}
                    />
                )}
            </span>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/import.js